import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slider,
  TextField,
  Typography,
} from '@mui/material';
import { UMButton } from '@urbanmetrix/um-kit';
import { Animate } from 'components/lib';
import { LeftDropDownArrow } from 'components/icons';
import {
  createCheckout,
  createCustomPlanCheckout,
  getBundles,
  unlockCustomBundle,
  type BillingBundle,
} from 'api/billingApi';

type PendingCustomBundle = {
  bundle: BillingBundle | null;
  unlockToken: string | null;
  loading: boolean;
} | null;

export function BuyVolume() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedReturnTo = searchParams.get('returnTo');
  const hasCustomReturnTo = Boolean(requestedReturnTo?.startsWith('/') && !requestedReturnTo.startsWith('//'));
  const returnTo = hasCustomReturnTo ? requestedReturnTo! : '/account/subscription';
  const [bundles, setBundles] = useState<BillingBundle[]>([]);
  const [selectedBundleIndex, setSelectedBundleIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [checkoutError, setCheckoutError] = useState('');
  const [customCode, setCustomCode] = useState('');
  const [customError, setCustomError] = useState('');
  const [pendingCustomBundle, setPendingCustomBundle] = useState<PendingCustomBundle>(null);
  const [buying, setBuying] = useState<string | null>(null);
  const submitting = useRef(false);

  useEffect(() => {
    let active = true;
    getBundles()
      .then((data) => {
        if (active) {
          const sortedBundles = [...data].sort((left, right) => left.quantity - right.quantity);
          setBundles(sortedBundles);
          setSelectedBundleIndex(getDefaultBundleIndex(sortedBundles));
        }
      })
      .catch(() => {
        if (active) setLoadError("We're experiencing server issues. Please try again later.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  async function buy(bundle: BillingBundle, unlockToken?: string) {
    if (submitting.current) return;
    submitting.current = true;
    setBuying(bundle.price.priceId);
    setCheckoutError('');
    try {
      if (!bundle.price.lookupKey) throw new Error('Missing price lookup key');
      const successUrl = `${window.location.origin}${returnTo}`;
      const cancelUrl = hasCustomReturnTo ? window.location.href : successUrl;
      const response = unlockToken
        ? await createCustomPlanCheckout(bundle.price.lookupKey, unlockToken, successUrl, cancelUrl, true)
        : await createCheckout(bundle.price.lookupKey, successUrl, cancelUrl, true);
      if (!response?.url) throw new Error('Missing checkout URL');
      window.location.assign(response.url);
    } catch {
      setCheckoutError('Unable to initialize checkout right now. Please try again later.');
      submitting.current = false;
      setBuying(null);
    }
  }

  async function checkCustomBundle() {
    const priceLookupKey = customCode.trim();
    if (!priceLookupKey || submitting.current) return;
    submitting.current = true;
    setCustomError('');
    setPendingCustomBundle({ bundle: null, unlockToken: null, loading: true });
    try {
      const response = await unlockCustomBundle(priceLookupKey);
      setPendingCustomBundle({ bundle: response.bundle, unlockToken: response.unlockToken, loading: false });
    } catch {
      setPendingCustomBundle(null);
      setCustomError('The bundle code you entered is not valid.');
    } finally {
      submitting.current = false;
    }
  }

  function closeCustomBundle() {
    if (buying) return;
    setPendingCustomBundle(null);
  }

  function formatPrice(bundle: BillingBundle) {
    const amount = bundle.price.unitAmount;
    return `${amount == null ? '—' : (amount / 100).toLocaleString('en-CH', { maximumFractionDigits: 2 })} ${bundle.price.currency?.toUpperCase() || '—'}`;
  }

  const selectedBundle = bundles[selectedBundleIndex] ?? bundles[0];
  const baseBundle = bundles[0];
  const savings = selectedBundle && baseBundle ? getBundleSavings(selectedBundle, baseBundle) : 0;

  return (
    <Animate type="pop" customClass="plan-shell">
      <div className="signup-plan-page-shell">
        <div className="signup-plan-page-panel">
          <div className="signup-plan-page-topbar">
            <button
              className="signup-plan-page-logout signup-plan-page-logout--left"
              type="button"
              onClick={() => navigate(returnTo)}
              aria-label="Back"
            >
              <LeftDropDownArrow size={18} />
              <span>Back</span>
            </button>
            <Typography
              variant="h4"
              className="signup-plan-page-title"
              sx={{ fontWeight: 700, color: 'black', fontSize: '1.8rem !important', lineHeight: 1.1 }}
            >
              Buy Volume
            </Typography>
          </div>
          <Typography sx={{ color: 'black', mb: 3 }}>
            Purchased volume never expires and adds to any volume you have previously purchased.
          </Typography>
          {loading ? (
            <Box sx={{ minHeight: 240, display: 'grid', placeItems: 'center' }}>
              <CircularProgress />
            </Box>
          ) : loadError || bundles.length === 0 ? (
            <Box
              role={loadError ? 'alert' : 'status'}
              sx={{ minHeight: 240, display: 'grid', placeItems: 'center', textAlign: 'center' }}
            >
              {loadError || 'No volume bundles are available right now.'}
            </Box>
          ) : (
            <div className="volume-bundle-selector">
              <div className="volume-bundle-selector__slider">
                <div className="volume-bundle-selector__row">
                  <strong>Volume</strong>
                  <div className="volume-bundle-selector__marks">
                    {bundles.map((bundle, index) => (
                      <span key={bundle.price.priceId} style={{ left: getBundleMarkPosition(index, bundles.length) }}>
                        {formatVolume(bundle.quantity)}
                      </span>
                    ))}
                  </div>
                </div>
                <Slider
                  aria-label="Volume bundle"
                  min={0}
                  max={bundles.length - 1}
                  step={1}
                  marks
                  value={selectedBundleIndex}
                  onChange={(_, value) => setSelectedBundleIndex(Array.isArray(value) ? value[0] : value)}
                />
                <div className="volume-bundle-selector__row">
                  <strong>Discount</strong>
                  <div className="volume-bundle-selector__marks volume-bundle-selector__marks--discounts">
                    {bundles.map((bundle, index) => (
                      <span key={bundle.price.priceId} style={{ left: getBundleMarkPosition(index, bundles.length) }}>
                        {getBundleDiscount(bundle, baseBundle)}%
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="volume-bundle-selector__summary">
                <Typography className="volume-bundle-selector__summary-label">Package price</Typography>
                <Typography className="volume-bundle-selector__price">
                  {selectedBundle.price.currency?.toUpperCase() || '—'} {formatMoney(selectedBundle.price.unitAmount)}
                </Typography>
                <Typography className="volume-bundle-selector__saving">
                  Save {formatMoney(savings)} {selectedBundle.price.currency?.toUpperCase() || '—'}
                </Typography>
                <UMButton
                  text="Buy"
                  fullWidth
                  customClassName="volume-bundle-selector__buy"
                  onClick={() => buy(selectedBundle)}
                  disabled={buying !== null || !selectedBundle.price.lookupKey}
                  loading={buying === selectedBundle.price.priceId}
                />
              </div>
            </div>
          )}
          {!loading && !loadError ? (
            <div className="signup-plan-page-custom-section">
              <div className="signup-plan-page-custom-row">
                <Typography className="signup-plan-page-custom-label" sx={{ color: '#111827', fontWeight: 500 }}>
                  Do you have a code for a custom bundle?
                </Typography>
                <TextField
                  value={customCode}
                  onChange={(event) => {
                    setCustomCode(event.target.value);
                    setCustomError('');
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      void checkCustomBundle();
                    }
                  }}
                  size="small"
                  className="signup-plan-page-custom-input"
                  type="search"
                  autoComplete="off"
                />
                {customCode.trim() ? (
                  <div className="signup-plan-page-custom-action">
                    <UMButton text="Check Custom Bundle" onClick={checkCustomBundle} />
                  </div>
                ) : null}
              </div>
              {customError ? (
                <Typography role="alert" sx={{ color: '#b42318', mt: 1 }}>
                  {customError}
                </Typography>
              ) : null}
            </div>
          ) : null}
          {checkoutError && (
            <Typography role="alert" sx={{ color: '#b42318', mt: 2, textAlign: 'center' }}>
              {checkoutError}
            </Typography>
          )}
        </div>
      </div>
      <Dialog open={Boolean(pendingCustomBundle)} onClose={closeCustomBundle} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>
          {pendingCustomBundle?.bundle?.productName || 'Unlocking custom bundle'}
        </DialogTitle>
        <DialogContent>
          {pendingCustomBundle?.loading ? (
            <Box sx={{ minHeight: 180, display: 'grid', placeItems: 'center' }}>
              <CircularProgress />
            </Box>
          ) : pendingCustomBundle?.bundle ? (
            <Box sx={{ display: 'grid', gap: 1.5, py: 1 }}>
              {pendingCustomBundle.bundle.productDescription ? (
                <Typography>{pendingCustomBundle.bundle.productDescription}</Typography>
              ) : null}
              <Typography sx={{ fontSize: '2rem !important', fontWeight: 700 }}>
                {pendingCustomBundle.bundle.quantity.toLocaleString('en-CH')} m³
              </Typography>
              <Typography sx={{ fontWeight: 700 }}>{formatPrice(pendingCustomBundle.bundle)}</Typography>
              <Typography sx={{ color: '#4b5563' }}>
                This volume never expires and adds to your existing volume.
              </Typography>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ padding: '0 24px 20px' }}>
          <UMButton
            text="Buy"
            fullWidth
            customClassName="signup-plan-page-cta-button signup-plan-page-cta-button--primary"
            disabled={!pendingCustomBundle?.bundle || !pendingCustomBundle.unlockToken || buying !== null}
            loading={Boolean(buying)}
            onClick={() =>
              pendingCustomBundle?.bundle &&
              pendingCustomBundle.unlockToken &&
              buy(pendingCustomBundle.bundle, pendingCustomBundle.unlockToken)
            }
          />
        </DialogActions>
      </Dialog>
    </Animate>
  );
}

function getDefaultBundleIndex(bundles: BillingBundle[]) {
  return Math.max(0, bundles.length - 2);
}

function getBundleMarkPosition(index: number, length: number) {
  return length <= 1 ? '50%' : `${(index / (length - 1)) * 100}%`;
}

function getBundleSavings(bundle: BillingBundle, baseBundle: BillingBundle) {
  if (bundle.price.unitAmount == null || baseBundle.price.unitAmount == null || baseBundle.quantity <= 0) return 0;
  return Math.max(
    0,
    Math.round((baseBundle.price.unitAmount / baseBundle.quantity) * bundle.quantity - bundle.price.unitAmount)
  );
}

function getBundleDiscount(bundle: BillingBundle, baseBundle: BillingBundle) {
  const fullPrice =
    baseBundle.price.unitAmount == null || baseBundle.quantity <= 0
      ? 0
      : (baseBundle.price.unitAmount / baseBundle.quantity) * bundle.quantity;
  if (!fullPrice || bundle.price.unitAmount == null) return 0;
  return Math.round((1 - bundle.price.unitAmount / fullPrice) * 100);
}

function formatMoney(amountInCents: number | null) {
  return amountInCents == null ? '—' : (amountInCents / 100).toLocaleString('en-CH', { maximumFractionDigits: 2 });
}

function formatVolume(quantity: number) {
  return `${(quantity / 1000).toLocaleString('en-CH')}k m³`;
}
